import React, { useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  Pressable,
  ScrollView,
  Platform,
} from 'react-native';

import { colors, fontWeight } from '../../theme';
import { X } from '../icons';

export const FORM_FIELD_OVERLAY_CARD_MAX_HEIGHT = 620;
export const FORM_FIELD_OVERLAY_BODY_MAX_HEIGHT = 460;
/** RN fade Modal duration; `onDismiss` waits this long after hide. */
export const FORM_FIELD_OVERLAY_FADE_MS = 300;
export const FORM_FIELD_OVERLAY_CLOSE_ICON_SIZE = 16;

export type FormFieldOverlayProps = {
  visible: boolean;
  onClose: () => void;
  /** Fires after the fade has finished (post-insert focus). */
  onDismiss?: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  closeAccessibilityLabel: string;
  scrimAccessibilityLabel?: string;
  /** Assist only. Help stays off so ⓘ behavior does not change. */
  closeOnEscape?: boolean;
  testID?: string;
};

/**
 * Shared ⓘ / text-assist chrome: fade Modal, scrim, card, title, X, scroll body, optional footer.
 * Help and assist pass different bodies; do not put Insert here.
 */
export const FormFieldOverlay: React.FC<FormFieldOverlayProps> = ({
  visible,
  onClose,
  onDismiss,
  title,
  children,
  footer,
  closeAccessibilityLabel,
  scrimAccessibilityLabel,
  closeOnEscape = false,
  testID,
}) => {
  const wasVisibleRef = useRef(visible);
  const dismissFiredRef = useRef(false);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const notifyDismissed = (): void => {
    if (dismissFiredRef.current) {
      return;
    }
    dismissFiredRef.current = true;
    onDismissRef.current?.();
  };

  useEffect(() => {
    if (visible) {
      wasVisibleRef.current = true;
      dismissFiredRef.current = false;
      if (fadeTimerRef.current != null) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
      return;
    }
    if (!wasVisibleRef.current) {
      return;
    }
    wasVisibleRef.current = false;
    fadeTimerRef.current = setTimeout(() => {
      fadeTimerRef.current = null;
      notifyDismissed();
    }, FORM_FIELD_OVERLAY_FADE_MS);
    return () => {
      if (fadeTimerRef.current != null) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    };
  }, [visible]);

  useEffect(() => {
    if (
      !closeOnEscape ||
      !visible ||
      Platform.OS !== 'web' ||
      typeof window === 'undefined' ||
      typeof window.addEventListener !== 'function'
    ) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [closeOnEscape, visible, onClose]);

  const scrimLabel = scrimAccessibilityLabel ?? closeAccessibilityLabel;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      accessibilityViewIsModal
    >
      <View style={styles.root} testID={testID}>
        <Pressable
          accessible
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={scrimLabel}
          testID="form-field-overlay-scrim"
        >
          <Text style={styles.scrimLabel}>{scrimLabel}</Text>
        </Pressable>
        {visible ? (
          <View style={styles.card}>
            <View style={styles.headerRow}>
              <Text
                style={styles.title}
                accessibilityRole="header"
                numberOfLines={3}
              >
                {title}
              </Text>
              <Pressable
                onPress={onClose}
                style={({ pressed }) => [
                  styles.close,
                  pressed && { opacity: 0.6 },
                ]}
                hitSlop={10}
                accessibilityLabel={closeAccessibilityLabel}
                accessibilityRole="button"
              >
                <X
                  size={FORM_FIELD_OVERLAY_CLOSE_ICON_SIZE}
                  color={colors.textSecondary}
                />
              </Pressable>
            </View>
            <ScrollView
              style={styles.bodyScroll}
              bounces={false}
              showsVerticalScrollIndicator
            >
              {children}
            </ScrollView>
            {footer ?? null}
          </View>
        ) : null}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
  },
  scrimLabel: {
    position: 'absolute' as const,
    width: 1,
    height: 1,
    opacity: 0,
    overflow: 'hidden' as const,
  },
  card: {
    width: '100%' as const,
    maxWidth: 420,
    maxHeight: FORM_FIELD_OVERLAY_CARD_MAX_HEIGHT,
    backgroundColor: colors.backgroundPrimary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.borderCard,
    overflow: 'hidden',
  },
  headerRow: {
    flexDirection: 'row' as const,
    alignItems: 'flex-start' as const,
    justifyContent: 'space-between' as const,
    gap: 12,
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 4,
  },
  title: {
    flex: 1,
    minWidth: 0,
    fontSize: 18,
    fontWeight: fontWeight.semibold,
    color: colors.textPrimary,
  },
  close: {
    padding: 4,
  },
  bodyScroll: {
    maxHeight: FORM_FIELD_OVERLAY_BODY_MAX_HEIGHT,
    paddingHorizontal: 20,
  },
});
